/**
 * Sensing Networks Management Types
 *
 * Defines all TypeScript interfaces and constants for network corridors,
 * network sites, and sensing stations management functionality in ConEx 360.
 */

// ============================================================================
// GPS Location Type
// ============================================================================

export interface GPSLocation {
  latitude: number;
  longitude: number;
}

// ============================================================================
// Network Technology Type
// ============================================================================

export interface NetworkTechnology {
  technology: '4G' | '5G' | 'LoRa' | 'Wifi' | 'RTK';
  spectrum: string;
  band: string;
  frequencyChannel: string;
  numberOfSectors: number;
  antennaType: string;
}

// ============================================================================
// Network Corridor Constants
// ============================================================================

export const NETWORK_CORRIDOR_STATUS_VARIANTS = {
  ACTIVE: 'default',
  INACTIVE: 'secondary',
  PLANNING: 'outline'
} as const;

export type NetworkCorridorStatusVariant = typeof NETWORK_CORRIDOR_STATUS_VARIANTS[keyof typeof NETWORK_CORRIDOR_STATUS_VARIANTS];

// ============================================================================
// Network Corridor Interface
// ============================================================================

/**
 * Network Corridor interface representing a network coverage corridor
 */
export interface NetworkCorridor {
  /** Unique identifier for the corridor */
  id: string;

  /** Business key - 10-digit corridor number */
  networkCorridorNumber?: number;

  /** Corridor name (1-200 characters) */
  name: string;

  /** Corridor description */
  description: string;

  /** US state abbreviation (e.g., 'CA', 'TX') */
  state?: string;

  /** Counties covered by this corridor (comma-separated string) */
  countiesCovered: string;

  /** Zipcodes covered by this corridor (comma-separated string) */
  zipcodesCovered: string;

  /** Site number identifier */
  siteNumber: string;

  /** Array of organization IDs that are network partners */
  networkPartner: string[];

  /** Number of potential sites in this corridor */
  numberOfPotentialSites: number;

  /** Expected corridor size (in square miles or similar unit) */
  expectedCorridorSize: number;

  /** Array of media/picture locations (S3 keys or URLs) */
  mediaLocations?: string[];

  /** Array of map image locations (S3 keys or URLs) */
  mapLocations?: string[];

  /** Unix timestamp of when corridor was created */
  createdAt: number;

  /** Unix timestamp of when corridor was last updated */
  updatedAt: number;
}

// ============================================================================
// Network Site Constants
// ============================================================================

export const INDOOR_STRUCTURE_TYPES = {
  BUILDING: 'building',
  CATTLE_SHED: 'cattleShed',
  BARN: 'barn',
  GREENHOUSE: 'greenhouse'
} as const;

export type IndoorStructureType = typeof INDOOR_STRUCTURE_TYPES[keyof typeof INDOOR_STRUCTURE_TYPES];

export const INDOOR_STRUCTURE_TYPE_OPTIONS = Object.values(INDOOR_STRUCTURE_TYPES);

export const OUTDOOR_STRUCTURE_TYPES = {
  STEEL_MONOPOLE: 'Steel Monopole',
  LATTICE: 'Lattice',
  GUYED: 'Guyed',
  WOODEN_MONOPOLE: 'Wooden Monopole'
} as const;

export type OutdoorStructureType = typeof OUTDOOR_STRUCTURE_TYPES[keyof typeof OUTDOOR_STRUCTURE_TYPES];

export const OUTDOOR_STRUCTURE_TYPE_OPTIONS = Object.values(OUTDOOR_STRUCTURE_TYPES);

export const NETWORK_TECHNOLOGIES = {
  FOUR_G: '4G',
  FIVE_G: '5G',
  LORA: 'LoRa',
  WIFI: 'Wifi',
  RTK: 'RTK',
} as const;

export type NetworkTechnologyType = typeof NETWORK_TECHNOLOGIES[keyof typeof NETWORK_TECHNOLOGIES];

export const NETWORK_TECHNOLOGY_OPTIONS = Object.values(NETWORK_TECHNOLOGIES);

export const BACKHAUL_OPTIONS = {
  FIBER: 'fiber',
  WIRELESS_MICROWAVE: 'wireless microwave',
  MESH: 'mesh',
  SATELLITE: 'satellite'
} as const;

export type BackhaulCapability = typeof BACKHAUL_OPTIONS[keyof typeof BACKHAUL_OPTIONS];

export const BACKHAUL_OPTION_VALUES = Object.values(BACKHAUL_OPTIONS);

// ============================================================================
// Network Site Interface
// ============================================================================

/**
 * Network Site interface representing a physical network site location
 */
export interface NetworkSite {
  /** Unique identifier for the site */
  id: string;

  /** Business key - network site number */
  networkSiteNumber?: number;

  /** ID of the parent network corridor (required) */
  corridorId: string;

  /** Site name (1-200 characters) */
  name: string;

  /** Physical address of the site */
  siteAddress: string;

  /** GPS location of the site */
  siteGeoLocation: GPSLocation;

  /** Additional location notes */
  locationNotes?: string;

  /** Whether site is indoor or outdoor */
  indoorOrOutdoorSite: 'indoor' | 'outdoor';

  /** Vertical structure height in feet/meters (required if outdoor) */
  verticalStructureHeight?: number;

  /** Outdoor structure type (required if outdoor) */
  outdoorVerticalStructureType?: OutdoorStructureType;

  /** Indoor structure type (required if indoor) */
  indoorStructureType?: IndoorStructureType;

  /** Array of network technologies deployed at this site */
  networkTechnologies: NetworkTechnology[];

  /** Backhaul capability type */
  backhaulCapability: BackhaulCapability;

  /** Whether shelter is available at the site */
  shelterAvailable: boolean;

  /** Whether cabinet is attached to the structure */
  cabinetAttached: boolean;

  /** Array of file URLs for site pictures */
  sitePictures: string[];

  /** Network site launch date (Unix timestamp) */
  networkSiteLaunchDate?: number;

  /** Unix timestamp of when site was created */
  createdAt: number;

  /** Unix timestamp of when site was last updated */
  updatedAt: number;
}

// ============================================================================
// Sensing Station Interface
// ============================================================================

/**
 * Sensing Station interface representing a sensing station location
 */
export interface SensingStation {
  /** Unique identifier for the station */
  id: string;

  /** Business key - station number */
  stationNumber?: number;

  /** Station name (1-200 characters) */
  name: string;

  /** GPS location of the station */
  geoLocation: GPSLocation;

  /** Address if available */
  addressIfAvailable?: string;

  /** Road intersections near the station */
  stationRoadIntersections?: string;

  /** Additional notes about the station */
  stationNotes?: string;

  /** Array of network site IDs that cover this station (many-to-many) */
  coveredBySites: string[];

  /** Array of file URLs for station pictures */
  stationPictures: string[];

  /** Unix timestamp of when station was created */
  createdAt: number;

  /** Unix timestamp of when station was last updated */
  updatedAt: number;
}

// ============================================================================
// Site Coverage Junction Table Type
// ============================================================================

/**
 * Site Coverage junction table type for many-to-many relationship
 * between Network Sites and Sensing Stations
 */
export interface SiteCoverage {
  siteId: string;
  stationId: string;
}

// ============================================================================
// API Request/Response Types — derived from Zod schemas (generatedSchema.ts is source of truth)
// ============================================================================

import type {
  GetNetworkCorridorsRegistry,
  GetNetworkCorridorByNumberRegistry,
  UpsertNetworkCorridorRegistry,
  DeleteNetworkCorridorRegistry,
  GetNetworkSitesRegistry,
  UpsertNetworkSiteRegistry,
  DeleteNetworkSiteRegistry,
  GetStationsRegistry,
  UpsertStationRegistry,
  DeleteStationRegistry,
  GetSiteCoverageForStationRegistry,
  UpdateSiteCoverageRegistry,
} from '@generated-schemas'
import type { InferRequest, InferResponse } from '@/types/registryTypes'

export type GetNetworkCorridorsResponse       = InferResponse<typeof GetNetworkCorridorsRegistry>
export type GetNetworkCorridorResponse        = InferResponse<typeof GetNetworkCorridorByNumberRegistry>
export type UpsertNetworkCorridorRequest      = InferRequest<typeof UpsertNetworkCorridorRegistry>['body']
export type UpsertNetworkCorridorResponse     = InferResponse<typeof UpsertNetworkCorridorRegistry>
export type DeleteNetworkCorridorRequest      = InferRequest<typeof DeleteNetworkCorridorRegistry>['body']
export type DeleteNetworkCorridorResponse     = InferResponse<typeof DeleteNetworkCorridorRegistry>
export type GetNetworkSitesResponse           = InferResponse<typeof GetNetworkSitesRegistry>
export type UpsertNetworkSiteRequest          = InferRequest<typeof UpsertNetworkSiteRegistry>['body']
export type UpsertNetworkSiteResponse         = InferResponse<typeof UpsertNetworkSiteRegistry>
export type DeleteNetworkSiteRequest          = InferRequest<typeof DeleteNetworkSiteRegistry>['body']
export type DeleteNetworkSiteResponse         = InferResponse<typeof DeleteNetworkSiteRegistry>
export type GetSensingStationsResponse        = InferResponse<typeof GetStationsRegistry>
export type UpsertSensingStationRequest       = InferRequest<typeof UpsertStationRegistry>['body']
export type UpsertSensingStationResponse      = InferResponse<typeof UpsertStationRegistry>
export type DeleteSensingStationRequest       = InferRequest<typeof DeleteStationRegistry>['body']
export type DeleteSensingStationResponse      = InferResponse<typeof DeleteStationRegistry>
export type GetSiteCoverageForStationResponse = InferResponse<typeof GetSiteCoverageForStationRegistry>
export type UpdateSiteCoverageRequest         = InferRequest<typeof UpdateSiteCoverageRegistry>['body']
export type UpdateSiteCoverageResponse        = InferResponse<typeof UpdateSiteCoverageRegistry>
